import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, LinkLine, WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {LinkIcon, linkToIcon} from 'components/shared/LinkIcon';
import type {ContextEngineSourceRow} from 'helpers/contextEngineRawResponse';
import {t} from 'i18next';
import type {MouseEvent} from 'react';
import {Fragment, useMemo} from 'react';

import {type ContextEngineSource, isAllowedHttpUrl} from './workstreamContextEngineChatApi';

/** Max visible characters on inline source chips (matches admin Context Engine preview). */
export const CONTEXT_ENGINE_INLINE_CHIP_LABEL_MAX = 28;

export type {ContextEngineSourceRow};

export function contextEngineSourcesToByIndexMap(
  sources: ReadonlyArray<ContextEngineSource | ContextEngineSourceRow> | undefined
): ReadonlyMap<string, ContextEngineSourceRow> {
  const m = new Map<string, ContextEngineSourceRow>();
  if (!sources?.length) {
    return m;
  }
  for (let i = 0; i < sources.length; i += 1) {
    const s = sources[i];
    const url = typeof s.url === 'string' ? s.url.trim() : '';
    const title = typeof s.title === 'string' ? s.title.trim() : '';
    const idxRaw = s.index;
    const index =
      idxRaw != null && String(idxRaw).trim() !== ''
        ? String(idxRaw).trim().toLowerCase()
        : String(i + 1);
    if (title || url) {
      m.set(index, {index, title: title || url, url});
    }
  }
  return m;
}

function truncateContextEngineChipLabel(label: string, maxLen: number): string {
  const trimmed = label.trim();
  if (trimmed.length <= maxLen) {
    return trimmed;
  }
  return `${trimmed.slice(0, Math.max(0, maxLen - 1))}…`;
}

/** True when the string contains at least one CE footnote marker (hash segment or numeric advanced). */
export function contextEngineTextHasCitationMarkers(text: string): boolean {
  return /\[\^[\da-fA-F]+(?::\d+)?\]/i.test(text);
}

/** Strips CE footnote markers from assistant text when inline citations / sources UI are hidden. */
export function stripContextEngineCitationMarkers(text: string): string {
  return text
    .replace(/\s*\[\^[\da-fA-F]+(?::\d+)?\]\s*/gi, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
}

const CONTEXT_ENGINE_CITATION_SPLIT_RE = /(\[\^[\da-fA-F]+(?::\d+)?\])/gi;

/** Inline DIG chip for a single [^…] marker (not a markdown link). */
export const ContextEngineCitationChip = ({
  hexKey,
  sourcesByIndex,
  interactive = true,
}: {
  hexKey: string;
  sourcesByIndex: ReadonlyMap<string, ContextEngineSourceRow>;
  interactive?: boolean;
}) => {
  const n = hexKey.toLowerCase();
  const row = sourcesByIndex.get(n);
  const label = row
    ? truncateContextEngineChipLabel(row.title, CONTEXT_ENGINE_INLINE_CHIP_LABEL_MAX)
    : `^${n}`;
  const openUrl = interactive && row && isAllowedHttpUrl(row.url) ? row.url.trim() : null;
  const aria =
    row && openUrl
      ? `Open source: ${row.title}`
      : row
        ? `Source: ${row.title}`
        : `Unknown source ${n}`;

  return (
    <span
      style={{
        display: 'inline-flex',
        verticalAlign: 'baseline',
        margin: '0 2px',
        maxWidth: '100%',
      }}
    >
      <Chip
        type="button"
        size="small"
        variant="standard"
        aria-label={aria}
        disabled={!openUrl}
        onClick={() => {
          if (openUrl) {
            window.open(openUrl, '_blank', 'noopener,noreferrer');
          }
        }}
        style={{maxWidth: 'min(100%, 240px)'}}
      >
        {openUrl ? (
          <Chip.IconAccessory>
            <UIIcon src={LinkLine} className={atoms({color: 'Text Subtle'})} />
          </Chip.IconAccessory>
        ) : null}
        <Chip.Content>{label}</Chip.Content>
      </Chip>
    </span>
  );
};

/**
 * Assistant markdown with CE footnotes rendered as DIG chips (not inline markdown links).
 * Text between markers is parsed as markdown in separate read-only editors so formatting is preserved.
 */
export const WorkstreamMarkdownWithCitationChips = ({
  value,
  sources,
  withLinks,
  paddingY,
  citationChipsInteractive = true,
  showCitationMarkers = true,
}: {
  value: string;
  sources?: ContextEngineSource[];
  withLinks?: boolean;
  paddingY?: boolean;
  citationChipsInteractive?: boolean;
  /** When false, footnote markers are removed from the body and chips are not rendered. */
  showCitationMarkers?: boolean;
}) => {
  const sourcesByIndex = useMemo(() => contextEngineSourcesToByIndexMap(sources), [sources]);

  const displayValue = showCitationMarkers ? value : stripContextEngineCitationMarkers(value);

  if (!contextEngineTextHasCitationMarkers(displayValue)) {
    return (
      <Box {...(paddingY ? {paddingY: '4'} : {})} style={{lineHeight: 1.4, width: '100%'}}>
        {displayValue.trim() ? (
          <RichTextArea
            disableBorder
            not100
            value={displayValue}
            editable={false}
            textColor="inherit"
            textSize="small"
            disableTables
            {...(withLinks ? {links: true, clickLinks: true} : {})}
          />
        ) : null}
      </Box>
    );
  }

  const parts = displayValue.split(CONTEXT_ENGINE_CITATION_SPLIT_RE);

  return (
    <Box
      {...(paddingY ? {paddingY: '4'} : {})}
      display="flex"
      flexWrap="wrap"
      alignItems="baseline"
      style={{
        lineHeight: 1.4,
        width: '100%',
        gap: '2px 6px',
      }}
    >
      {parts.map((part, idx) => {
        const m = /^\[\^([\da-fA-F]+)(?::\d+)?\]$/i.exec(part);
        if (m) {
          return (
            <ContextEngineCitationChip
              key={`ce-cite-${idx}`}
              hexKey={m[1]}
              sourcesByIndex={sourcesByIndex}
              interactive={citationChipsInteractive}
            />
          );
        }
        if (!part) {
          return null;
        }
        return (
          <Box
            key={`ce-md-${idx}`}
            display="inline-block"
            style={{
              minWidth: 0,
              maxWidth: '100%',
              verticalAlign: 'baseline',
            }}
          >
            <RichTextArea
              disableBorder
              not100
              value={part}
              editable={false}
              textColor="inherit"
              textSize="small"
              disableTables
              {...(withLinks ? {links: true, clickLinks: true} : {})}
            />
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Renders assistant text with inline DIG chips for Context Engine footnotes:
 * numeric advanced citations [^6:23] and chat_v3 uuid-segment refs [^24b7d762].
 */
export const ContextEngineAssistantTextWithCitationChips = ({
  text,
  sourcesByIndex,
}: {
  text: string;
  sourcesByIndex: ReadonlyMap<string, ContextEngineSourceRow>;
}) => {
  const parts = text.split(CONTEXT_ENGINE_CITATION_SPLIT_RE);
  return (
    <>
      {parts.map((part, idx) => {
        const m = /^\[\^([\da-fA-F]+)(?::\d+)?\]$/i.exec(part);
        if (m) {
          return (
            <ContextEngineCitationChip
              key={idx}
              hexKey={m[1]}
              sourcesByIndex={sourcesByIndex}
              interactive
            />
          );
        }
        return <Fragment key={idx}>{part}</Fragment>;
      })}
    </>
  );
};

/** Same pattern as admin Context Engine test: full source list in a menu. */
export const ContextEngineSourcesMenuButton = ({sources}: {sources: ContextEngineSourceRow[]}) => {
  const count = sources.length;
  if (count === 0) {
    return null;
  }
  return (
    <Menu.Wrapper closeOnSelection>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button
            type="button"
            variant="outline"
            size="small"
            {...getTriggerProps()}
            style={{alignSelf: 'flex-start'}}
          >
            {`${count} source${count === 1 ? '' : 's'}`}
          </Button>
          <Menu.Content
            {...getContentProps()}
            placement="top-start"
            minWidth="matchContainingWidth"
            style={{
              maxWidth: 440,
              maxHeight: 360,
              overflow: 'auto',
            }}
          >
            <Menu.Segment withLabel="Sources">
              {sources.map((s) => (
                <Menu.ActionItem
                  key={`${s.index}-${s.url}-${s.title}`}
                  onClick={() => {
                    try {
                      const u = new URL(s.url);
                      if (u.protocol === 'http:' || u.protocol === 'https:') {
                        window.open(s.url, '_blank', 'noopener,noreferrer');
                      }
                    } catch {
                      // ignore invalid URL
                    }
                  }}
                >
                  <Menu.RowTitle>{s.title || s.url || `Source ${s.index}`}</Menu.RowTitle>
                  {isAllowedHttpUrl(s.url) ? (
                    <Menu.RowSubtitle
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.url}
                    </Menu.RowSubtitle>
                  ) : null}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

export const WorkstreamChatContextWorkstreamChip = ({
  name,
  tooltipTitle,
  disabled,
  onClick,
}: {
  name: string;
  tooltipTitle: string;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) => (
  <Tooltip placement="bottom" title={tooltipTitle}>
    <Chip
      type="button"
      size="small"
      variant="standard"
      aria-label={name}
      disabled={disabled}
      onClick={onClick}
    >
      <Chip.IconAccessory>
        <UIIcon src={WorkstreamsLine} size="small" />
      </Chip.IconAccessory>
      <Chip.Content>
        <Truncate maxWidth={175}>{name}</Truncate>
      </Chip.Content>
    </Chip>
  </Tooltip>
);

export const WorkstreamChatContextAttachmentsMenuChip = ({
  attachments,
  onDismiss,
  disabled,
}: {
  attachments: Array<{url: string; title: string}>;
  onDismiss: (url: string) => void;
  disabled?: boolean;
}) => {
  const count = attachments.length;
  if (count === 0) {
    return null;
  }

  const label = t('workstream_resources', {count});

  return (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents closeOnSelection={false}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Chip
            {...getTriggerProps()}
            size="small"
            variant="standard"
            withDropdownIcon
            disabled={disabled}
            aria-label={label.toString()}
          >
            <Chip.Content>
              <Truncate maxWidth={175}>{label}</Truncate>
            </Chip.Content>
          </Chip>
          <Menu.Content
            {...getContentProps()}
            placement="bottom-start"
            minWidth="matchContainingWidth"
          >
            <Menu.Segment>
              {attachments.map((attachment) => {
                const openUrl = isAllowedHttpUrl(attachment.url) ? attachment.url.trim() : null;
                const displayTitle = (attachment.title || attachment.url).trim() || attachment.url;

                return (
                  <Menu.ActionItem
                    key={attachment.url}
                    disabled={!openUrl}
                    onClick={() => {
                      if (!openUrl) {
                        return;
                      }
                      window.open(openUrl, '_blank', 'noopener,noreferrer');
                    }}
                    withLeftAccessory={
                      <LinkIcon
                        icon={linkToIcon(attachment.url)}
                        size="small"
                        className={atoms({color: 'Text Subtle'})}
                      />
                    }
                    withRightAccessory={
                      disabled ? undefined : (
                        <IconButton
                          variant="transparent"
                          size="small"
                          aria-label={t('workstream_chat_remove_context_resource', {
                            title: displayTitle,
                          })}
                          onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            event.preventDefault();
                            onDismiss(attachment.url);
                          }}
                        >
                          <UIIcon
                            src={CloseLine}
                            size="small"
                            className={atoms({color: 'Text Subtle'})}
                          />
                        </IconButton>
                      )
                    }
                  >
                    <Menu.RowTitle>{displayTitle}</Menu.RowTitle>
                    {openUrl ? (
                      <Menu.RowSubtitle
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {attachment.url}
                      </Menu.RowSubtitle>
                    ) : null}
                  </Menu.ActionItem>
                );
              })}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
